"use client";

import * as React from "react";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";

const POPUP_DISMISSED_STORAGE_KEY = "ms-popup-dismissed";

function getStoredDismissal(): boolean {
	try {
		return window.localStorage.getItem(POPUP_DISMISSED_STORAGE_KEY) === "true";
	} catch {
		return false;
	}
}

function setStoredDismissal(): void {
	try {
		window.localStorage.setItem(POPUP_DISMISSED_STORAGE_KEY, "true");
	} catch {}
}

/**
 * Positions the popup and remembers whether the visitor dismissed it.
 *
 * Kept separate from the popup itself so the rich text stays server rendered.
 *
 * @param children - Popup contents.
 */
export function PopupContainer({ children }: { children: React.ReactNode }) {
	const t = useTranslations("popup");
	const [open, setOpen] = React.useState(false);

	React.useEffect(() => {
		setOpen(!getStoredDismissal());
	}, []);

	const handleDismiss = React.useCallback(() => {
		setStoredDismissal();
		setOpen(false);
	}, []);

	if (!open) return null;

	return (
		<div className="fixed right-3 bottom-3 z-1000 w-[min(20rem,calc(100vw-1.5rem))]">
			{children}
			<button type="button" className="absolute top-2 right-2 cursor-pointer" onClick={handleDismiss} aria-label={t("a11y.close-label")}>
				<X className="size-4" aria-hidden />
			</button>
		</div>
	);
}
